import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { HOSPITALS_LIST, ASSIGN_PATIENT } from '../constants/api';
import { apiUtils, errorUtils } from '../utils';
import { CSS_CLASSES, MESSAGES } from '../constants/app';
import { InlineLoading, Spinner } from './loading/LoadingIndicators';

interface Hospital {
  _id: string;
  full_name: string;
  email: string;
  role: 'admin' | 'super_admin';
}

interface AssignPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  currentHospitalId?: string;
  onAssignmentSuccess: () => void;
}

export default function AssignPatientModal({
  isOpen,
  onClose,
  patientId,
  patientName,
  currentHospitalId,
  onAssignmentSuccess,
}: AssignPatientModalProps) {
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [selectedHospitalId, setSelectedHospitalId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [fetchingHospitals, setFetchingHospitals] = useState(false);
  const [error, setError] = useState<string>('');

  // Fetch hospitals list when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchHospitals();
      setSelectedHospitalId(currentHospitalId || '');
      setError('');
    }
  }, [isOpen, currentHospitalId]);

  const fetchHospitals = async () => {
    setFetchingHospitals(true);
    setError('');
    try {
      const response = await fetch(
        `${HOSPITALS_LIST}?page=1&limit=100`,
        apiUtils.createRequestOptions('GET')
      );
      const result = await apiUtils.handleResponse<{ data: Hospital[] }>(response);
      // Filter out super_admin hospitals from the list
      const filteredHospitals = (result.data || []).filter(
        (hospital) => hospital.role !== 'super_admin'
      );
      setHospitals(filteredHospitals);
    } catch (error) {
      const errorMessage = errorUtils.getErrorMessage(error);
      setError(`Failed to load hospitals: ${errorMessage}`);
      errorUtils.logError(error, 'AssignPatientModal - fetchHospitals');
    } finally {
      setFetchingHospitals(false);
    }
  };

  const handleAssign = async () => {
    const isUnassignment = !selectedHospitalId || selectedHospitalId === '';
    
    // Only check for duplicate assignment when assigning (not unassigning)
    if (!isUnassignment && selectedHospitalId === currentHospitalId) {
      setError('Patient is already assigned to this hospital');
      return;
    }

    // Check if trying to unassign an already unassigned patient
    if (isUnassignment && !currentHospitalId) {
      setError('Patient is already unassigned from any hospital');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        `${ASSIGN_PATIENT}/${patientId}/assign`,
        apiUtils.createRequestOptions('PATCH', {
          hospitalId: isUnassignment ? null : selectedHospitalId,
        })
      );

      await apiUtils.handleResponse(response);
      
      // Success - close modal and refresh patient list
      onAssignmentSuccess();
      onClose();
    } catch (error) {
      const errorMessage = errorUtils.getErrorMessage(error);
      setError(errorMessage);
      errorUtils.logError(error, 'AssignPatientModal - handleAssign');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-3 sm:items-center">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            {currentHospitalId ? 'Assign/Unassign Patient' : 'Assign Patient to Hospital'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto">
          <p className="text-gray-600 mb-4">
            {currentHospitalId ? (
              <>Assign or unassign <span className="font-medium">{patientName}</span> from a hospital:</>
            ) : (
              <>Assign <span className="font-medium">{patientName}</span> to a hospital:</>
            )}
          </p>

          {fetchingHospitals ? (
            <div className="flex items-center justify-center py-8">
              <InlineLoading message={MESSAGES.LOADING.HOSPITALS} />
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label
                  htmlFor="hospital-select"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Select Hospital
                </label>
                <select
                  id="hospital-select"
                  value={selectedHospitalId}
                  onChange={(e) => setSelectedHospitalId(e.target.value)}
                  className={`${CSS_CLASSES.INPUTS.BASE} w-full`}
                  disabled={loading}
                >
                  <option value="">
                    {currentHospitalId ? '-- Unassign from hospital --' : '-- Select a hospital --'}
                  </option>
                  {hospitals.map((hospital) => (
                    <option key={hospital._id} value={hospital._id}>
                      {hospital.full_name} {hospital.email ? `(${hospital.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {error && (
                <div className={`${CSS_CLASSES.TEXT.ERROR} mb-4`}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-3 p-4 sm:p-6 border-t bg-gray-50 sm:flex-row sm:items-center sm:justify-end">
          <button
            onClick={onClose}
            className="min-h-11 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={loading || fetchingHospitals}
            className={`min-h-11 px-4 py-2 rounded-lg font-medium transition-colors ${
              loading || fetchingHospitals
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : CSS_CLASSES.BUTTONS.PRIMARY
            }`}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Spinner size="sm" variant="onPrimary" />
                {!selectedHospitalId && currentHospitalId ? 'Unassigning...' : 'Assigning...'}
              </span>
            ) : (
              !selectedHospitalId && currentHospitalId ? 'Unassign' : 'Assign'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

